import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    CircularProgress,
    Alert,
    Chip,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as RadioButtonUncheckedIcon,
    Schedule as ScheduleIcon,
    Assignment as AssignmentIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import todoImg from './todo.jpeg';
import wipImg from './wip.png';
import doneImg from './done.jpg';

export default function ShowProductProgress() {
    const [stages, setStages] = useState([]);
    const [taskInstances, setTaskInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const {selectedTaskObj} = useSelector((state) => state.appslice.wkflow);

    useEffect(() => {
        fetchStagesAndTasks();
    }, []);

    const fetchStagesAndTasks = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch stages
            const stagesResponse = await axios.get(
                'https://api.platform.appmachine.in/stages/workflowid/' + selectedTaskObj.workflowid + '/workflowid',
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('JWT')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Fetch task instances
            const tasksResponse = await axios.get(
                'https://api.platform.appmachine.in/taskinstances/boobjid/' + selectedTaskObj.boobjid + '/boobjid',
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('JWT')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (stagesResponse.data && stagesResponse.data.list) {
                setStages(stagesResponse.data.list);
            } else {
                setStages([]);
            }

            if (tasksResponse.data && tasksResponse.data.list) {
                setTaskInstances(tasksResponse.data.list);
            } else {
                setTaskInstances([]);
            }
        } catch (error) {
            console.error('Error fetching stages and tasks:', error);
            setError('Failed to load workflow stages and tasks. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getTasksForStage = (stageId) => {
        return taskInstances.filter(task => task.stageid === stageId)
            .sort((a, b) => (a.seqno ?? 0) - (b.seqno ?? 0)); // Sort by seqno ascending
    };

    const getTaskStatusColor = (taskStatus) => {
        switch (taskStatus) {
            case 'DONE':
            case 'COMPLETED':
                return 'success';
            case 'WIP':
            case 'IN_PROGRESS':
                return 'warning';
            case 'REJECTED':
                return 'error';
            default:
                return 'default';
        }
    };

    // Helper to get status image by taskstatusid (similar to OrderFullfillmentWkf2)
    const getStatusImage = (statusId) => {
        if (statusId === 1) return todoImg;
        if (statusId === 2) return wipImg;
        if (statusId === 3) return doneImg;
        return todoImg;
    };

    const getStageStatusId = (stageId) => {
        const stageTasks = getTasksForStage(stageId);
        
        if (stageTasks.some(task => task.taskstatusid === 2)) {
            return 2; // WIP
        } else if (stageTasks.length > 0 && stageTasks.every(task => task.taskstatusid === 3)) {
            return 3; // DONE
        } else {
            return 1; // TODO
        }
    };

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '400px' 
            }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ ml: 2 }}>
                    Loading workflow progress...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!stages || stages.length === 0) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">
                    No workflow stages found.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{}}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Workflow Progress
            </Typography>

            {/* Stages in Single Row - Following OrderFullfillmentWkf2 pattern */}
            <Grid
                container
                direction="row"
                sx={{
                    //width: '100%',
                    backgroundColor: 'secondary.100',
                    mb: 3,
                    maxHeight: '75vh', // Adjust as needed for your layout
                    overflowY: 'auto',
                    maxWidth:'90vw'
                }}
            >
                {stages.map((stage, index) => {
                    const stageTasks = getTasksForStage(stage.id);
                    const stageStatusId = getStageStatusId(stage.id);

                    return (
                        <Grid item 
                            lg={stage.id===30 ? 4 : 2} md={2} sm={2} xs={6}
                            key={stage.id || index}
                            sx={{
                                width: '100%',
                                p: 1,
                                borderRadius: 2,
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                '&:hover': {
                                    backgroundColor: '#e3f2fd'
                                },
                                cursor: 'pointer'
                            }}
                        >
                            <Box sx={{ width: '100%', backgroundColor: 'white', borderRadius: 2, p: 1 }}>
                                {/* Stage Header with Icon and Title */}
                                <Grid container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                                    <img
                                        src={getStatusImage(stageStatusId)}
                                        alt={stage.name || stage.title}
                                        style={{ width: 32, height: 32, objectFit: 'contain' }}
                                    />
                                    &nbsp;&nbsp;{stage.name || stage.title || `Stage ${index + 1}`}
                                </Grid>
                                
                                {/* Tasks List */}
                                {stage.id === 30 ? (
                                    <Grid container spacing={1} sx={{ mt: 1, width: '100%' }}>
                                        {stageTasks.map((task, taskIndex) => (
                                            <Grid item xs={12} sm={6} key={taskIndex}>
                                                <Box sx={{ p: 0.5 }}>
                                                    <Grid container direction="row" sx={{
                                                        width: '100%',
                                                        borderRadius: 1,
                                                        textAlign: 'left',
                                                        p: 0.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start',
                                                        backgroundColor: task.taskstatusname === "WIP" ? '#e3f2fd' : '#f5f5f5',
                                                        border: task.taskstatusname === "WIP" ? '2px solid #1976d2' : '',
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        }
                                                    }}>
                                                        <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" sx={{ width: '100%' }}>
                                                            <Grid item lg={12} xs={10} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                                                                <Grid container direction="column" sx={{ width: '100%' }}>
                                                                    <Grid item lg={12} xs={12}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'left', fontSize: 13 }}>
                                                                            {task.title || task.taskname}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid container justifyContent="flex-start" item lg={12} xs={12}>
                                                                        <Grid item lg={8} xs={8} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'top' }}>
                                                                            <Typography variant="body1" color="textSecondary" gutterBottom>
                                                                                {task.performerrole.replace('ROLE_PHARMA_OMS_', '')}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item lg={4} xs={4} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'top' }}>
                                                                            <img
                                                                                src={getStatusImage(task.taskstatusid)}
                                                                                alt={task.taskstatusname}
                                                                                style={{ width: 20, height: 20, objectFit: 'contain' }}
                                                                            />
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Grid container direction="column" sx={{ mt: 1, width: '100%' }}>
                                        {stageTasks.map((task, taskIndex) => (
                                            <Grid item lg={12} md={12} sm={12} xs={12} sx={{ width: '100%' }} key={taskIndex}>
                                                <Box sx={{ p: 0.5 }}>
                                                    <Grid container direction="row" sx={{
                                                        width: '100%',
                                                        borderRadius: 1,
                                                        textAlign: 'left',
                                                        p: 0.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start',
                                                        backgroundColor: task.taskstatusname === "WIP" ? '#e3f2fd' : '#f5f5f5',
                                                        border: task.taskstatusname === "WIP" ? '2px solid #1976d2' : '',
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        }
                                                    }}>
                                                        <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" sx={{ width: '100%' }}>
                                                            <Grid item lg={12} xs={10} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                                                                <Grid container direction="column" sx={{ width: '100%' }}>
                                                                    <Grid item lg={12} xs={12}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'left', fontSize: 13 }}>
                                                                            {task.title || task.taskname}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid container justifyContent="flex-start" item lg={12} xs={12}>
                                                                        <Grid item lg={8} xs={8} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'top' }}>
                                                                            <Typography variant="body1" color="textSecondary" gutterBottom>
                                                                                {task.performerrole.replace('ROLE_PHARMA_OMS_', '')}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item lg={4} xs={4} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'top' }}>
                                                                            <img
                                                                                src={getStatusImage(task.taskstatusid)}
                                                                                alt={task.taskstatusname}
                                                                                style={{ width: 20, height: 20, objectFit: 'contain' }}
                                                                            />
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>

            
        </Box>
    );
}